// Plan performance page. Reachable from the brand Plans card and the
// plan detail view. Reads GET /api/marketing-plans/:id/performance.

import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { Button, HStack, Spinner, Text } from '@chakra-ui/react';
import { useBrand } from '../brand/BrandContext';
import { PlanPerformanceView } from '../components/marketingPlan/PlanPerformanceView';
import { PageHeader } from '../shell/PageHeader';
import { planBrandMismatch } from '../marketingPlans/detail';
import {
  getPlanPerformance, planPerformanceEnabled, planSuggestionsEnabled,
  type PlanPerformance
} from '../marketingPlans/performance';
import { acceptPlanSuggestion, getPlanSuggestions, type PlanSuggestionView } from '../marketingPlans/drafts';

type LoadState = {
  loading: boolean;
  model: PlanPerformance | null;
  missing: boolean;
  notFound: boolean;
  error: string | null;
};

const NOT_YOUR_BRAND = 'This plan was not found, or it is not your brand.';

export function PlanPerformancePage() {
  const { planId } = useParams();
  const [search] = useSearchParams();
  const { activeBrand } = useBrand();
  const enabled = planPerformanceEnabled();
  const queryBrand = search.get('brandId');
  const activeId = activeBrand?.id ?? null;
  const brandMismatch = planBrandMismatch(queryBrand, activeId);
  const brandId = !enabled || brandMismatch ? null : (queryBrand || activeId);
  const suggestionBrandId = enabled && queryBrand && queryBrand === activeId ? queryBrand : null;
  const revision = search.get('revision');
  const [state, setState] = useState<LoadState>({ loading: true, model: null, missing: false, notFound: false, error: null });
  const suggestionsOn = planSuggestionsEnabled();
  const [suggestions, setSuggestions] = useState<PlanSuggestionView[] | null | undefined>(undefined);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !brandId || !planId || brandMismatch) {
      setState({ loading: false, model: null, missing: false, notFound: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ loading: true, model: null, missing: false, notFound: false, error: null });
    void getPlanPerformance(brandId, planId, revision).then((result) => {
      if (cancelled) return;
      setState({
        loading: false,
        model: result.performance,
        missing: result.missing,
        notFound: result.notFound,
        error: result.error
      });
    });
    return () => { cancelled = true; };
  }, [enabled, brandId, planId, revision, brandMismatch]);

  useEffect(() => {
    if (!suggestionsOn || !suggestionBrandId || !planId) {
      setSuggestions(undefined);
      setSuggestionsLoading(false);
      return;
    }
    let cancelled = false;
    setSuggestionsLoading(true);
    setSuggestions(undefined);
    void getPlanSuggestions(suggestionBrandId, planId).then((next) => {
      if (cancelled) return;
      setSuggestions(next);
      setSuggestionsLoading(false);
    });
    return () => { cancelled = true; };
  }, [suggestionsOn, suggestionBrandId, planId]);

  return (
    <>
      <PageHeader
        eyebrow="Performance"
        title={state.model?.title ?? 'Plan performance'}
        description="Forecast against actual results for this plan."
        secondaryAction={<Button as={RouterLink} to="/brand" variant="outline">Back to brand</Button>}
      />
      {!enabled && <Muted>Plan performance isn't available yet.</Muted>}
      {enabled && brandMismatch && <Text fontSize="sm" data-testid="performance-not-found">{NOT_YOUR_BRAND}</Text>}
      {enabled && !brandMismatch && !planId && <Muted>Plan performance isn't available yet.</Muted>}
      {enabled && !brandMismatch && planId && !brandId && <Muted>Select a brand to open plan performance.</Muted>}
      {enabled && planId && brandId && state.loading && (
        <HStack data-testid="performance-loading">
          <Spinner size="sm" />
          <Text fontSize="sm" color="brand.muted">Loading performance…</Text>
        </HStack>
      )}
      {enabled && planId && brandId && state.error && (
        <Text fontSize="sm" color="red.600" data-testid="performance-error">{state.error}</Text>
      )}
      {enabled && planId && brandId && !state.loading && !state.error && state.notFound && (
        <Text fontSize="sm" data-testid="performance-not-found">{NOT_YOUR_BRAND}</Text>
      )}
      {enabled && planId && brandId && !state.loading && !state.error && !state.notFound && (state.missing || !state.model) && (
        <Muted>Plan performance isn't available yet.</Muted>
      )}
      {enabled && state.model && (
        <PlanPerformanceView
          model={state.model}
          suggestionsEnabled={suggestionsOn}
          suggestions={suggestionsOn ? suggestions : undefined}
          suggestionsLoading={suggestionsOn && suggestionsLoading}
          onAcceptSuggestion={suggestionBrandId && planId ? async (suggestion) => {
            if (!suggestion.id) return { planId: null, duplicate: false, error: null };
            return acceptPlanSuggestion(suggestionBrandId, planId, suggestion.id);
          } : undefined}
        />
      )}
    </>
  );
}

function Muted({ children }: { children: string }) {
  return <Text fontSize="sm" color="brand.muted">{children}</Text>;
}
